import { Injectable, inject, signal, computed } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import {
  Firestore,
  collection,
  collectionData,
  doc,
  onSnapshot,
} from '@angular/fire/firestore';
import { AuthService } from './auth.service';
import { PermissionService } from './permission.service';
import { PropertyService, AdminProperty } from './property.service';
import { PermissionDocument } from './permission.service';
import { TranslationService } from './translation.service';
import { ConstantRole } from '../models/constant-role.model';
import { CollectionTables } from '../models/CollectionTables.model';

export type UserRole = 'manager' | 'admin' | 'clerk' | 'maintenance' | 'agent';

function uniquePropertiesFromPermissions(
  permissions: PermissionDocument[],
  administratorId: string
): AdminProperty[] {
  const map = new Map<string, AdminProperty>();
  for (const p of permissions) {
    if (!p.propertyId || map.has(p.propertyId)) continue;
    map.set(p.propertyId, {
      id: p.propertyId,
      name: (p.propertyName || '').trim() || 'Property',
      businessId: p.businessId || p.administratorId || administratorId,
      administratorId: p.administratorId || administratorId,
    });
  }
  return Array.from(map.values());
}

export interface UserContextState {
  role: UserRole | null;
  employeeId: string | null;
  administratorId: string | null;
  permittedProperties: AdminProperty[];
  loaded: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class UserContextService {
  private authService = inject(AuthService);
  private permissionService = inject(PermissionService);
  private propertyService = inject(PropertyService);
  private firestore = inject(Firestore);
  private translationService = inject(TranslationService);

  private rolesFirestoreSub: Subscription | null = null;

  private readonly state = new BehaviorSubject<UserContextState>({
    role: null,
    employeeId: null,
    administratorId: null,
    permittedProperties: [],
    loaded: false,
  });

  readonly role = signal<UserRole | null>(null);
  readonly employeeId = signal<string | null>(null);
  readonly administratorId = signal<string | null>(null);
  readonly permittedProperties = signal<AdminProperty[]>([]);
  readonly loaded = signal(false);

  /** Roles from Firestore `constants/roles/{en|ar}`, sorted by `level` ascending. */
  readonly roles = signal<ConstantRole[]>([]);

  readonly isManager = computed(() => this.role() === 'manager');
  readonly isAdmin = computed(() => this.role() === 'admin');
  readonly isClerk = computed(() => this.role() === 'clerk');
  readonly isMaintenance = computed(() => {
    const r = (this.role() || '').toLowerCase();
    return r === 'agent' || r === 'maintenance';
  });

  /** True when the signed-in user has a linked `employees` document (org access). */
  readonly hasOrganizationAccess = computed(() => this.role() !== null);

  /** Fired when the employee document is removed in Firestore (e.g. deleted in admin web or mobile). */
  private readonly accessRevokedSubject = new Subject<void>();
  readonly accessRevoked$ = this.accessRevokedSubject.asObservable();

  private initInFlight: Promise<void> | null = null;
  private employeeDocUnsubscribe: (() => void) | null = null;

  /** Properties the user can access (all for manager, permitted for admin/clerk) */
  readonly accessibleProperties = computed(() => {
    if (this.role() === 'manager') {
      return this.permittedProperties(); // For manager, we load all in loadPropertiesForManager
    }
    return this.permittedProperties();
  });

  constructor() {
    this.translationService.currentLang$
      .pipe(distinctUntilChanged())
      .subscribe((lang) => this.bindRolesToFirestore(lang));
  }

  private bindRolesToFirestore(lang: string): void {
    this.rolesFirestoreSub?.unsubscribe();
    const suffix = (lang || 'en').toLowerCase().startsWith('ar') ? 'ar' : 'en';
    const rolesCollection = collection(
      this.firestore,
      `constants/roles/${suffix}`
    );
    this.rolesFirestoreSub = collectionData(rolesCollection, {
      idField: 'id',
    }).subscribe({
      next: (rows) => {
        const list = (rows as ConstantRole[]).map((r) => ({
          ...r,
          level: Number(r.level) || 999,
        }));
        list.sort((a, b) => a.level - b.level);
        this.roles.set(list);
      },
      error: (err) => {
        console.error('UserContextService: roles subscription failed', err);
        this.roles.set([]);
      },
    });
  }

  /**
   * Initialize user context when user is authenticated.
   * Call from MainLayout or when entering /app routes.
   */
  async initialize(): Promise<void> {
    if (this.initInFlight) {
      return this.initInFlight;
    }
    this.initInFlight = this.performInitialize().finally(() => {
      this.initInFlight = null;
    });
    return this.initInFlight;
  }

  /** Skip work if context is already loaded (guards, repeated navigation). */
  async ensureInitialized(): Promise<void> {
    if (!this.loaded()) {
      await this.initialize();
    }
  }

  private async performInitialize(): Promise<void> {
    this.stopEmployeeDocListener();
    const user = this.authService.getCurrentUser();
    if (!user) {
      this.setState({
        role: null,
        employeeId: null,
        administratorId: null,
        permittedProperties: [],
        loaded: true,
      });
      return;
    }

    try {
      const employee = await this.authService.getCurrentEmployee(user.uid);
      if (!employee) {
        this.setState({
          role: null,
          employeeId: null,
          administratorId: null,
          permittedProperties: [],
          loaded: true,
        });
        return;
      }

      await this.hydrateEmployeeContext(employee);
      this.startEmployeeDocListener(employee.id, employee.administratorId);
    } catch (err) {
      console.error('UserContextService: Failed to initialize', err);
      this.setState({
        role: null,
        employeeId: null,
        administratorId: null,
        permittedProperties: [],
        loaded: true,
      });
    }
  }

  private async hydrateEmployeeContext(employee: {
    id: string;
    role: string;
    administratorId: string;
  }): Promise<void> {
    let roleNorm = (employee.role || 'clerk').toLowerCase();
    if (roleNorm === 'maintenance') {
      roleNorm = 'agent';
    }
    const administratorId = employee.administratorId;

    if (roleNorm === 'manager') {
      const permRows = await firstValueFrom(
        this.permissionService.getByAdministratorId(administratorId)
      );
      let permitted = uniquePropertiesFromPermissions(permRows, administratorId);
      if (permitted.length === 0 && administratorId) {
        permitted = await firstValueFrom(
          this.propertyService.getPropertiesByAdministratorId(administratorId)
        );
      }
      this.setState({
        role: 'manager',
        employeeId: employee.id,
        administratorId,
        permittedProperties: permitted,
        loaded: true,
      });
      return;
    }

    if (roleNorm === 'admin' || roleNorm === 'clerk' || roleNorm === 'agent') {
      const permissions = await firstValueFrom(
        this.permissionService.getByEmployeeId(employee.id)
      );
      const permitted = uniquePropertiesFromPermissions(
        permissions,
        administratorId || ''
      );
      this.setState({
        role: roleNorm as UserRole,
        employeeId: employee.id,
        administratorId,
        permittedProperties: permitted,
        loaded: true,
      });
      return;
    }

    this.setState({
      role: roleNorm as UserRole,
      employeeId: employee.id,
      administratorId,
      permittedProperties: [],
      loaded: true,
    });
  }

  private startEmployeeDocListener(
    employeeId: string,
    administratorId: string
  ): void {
    if (!employeeId || !administratorId) return;
    this.stopEmployeeDocListener();
    // Try businesses collection first (new structure), fall back to administrators (legacy)
    // We resolve the collection asynchronously and start the listener once known
    void this.resolveAndStartListener(employeeId, administratorId);
  }

  private async resolveAndStartListener(employeeId: string, administratorId: string): Promise<void> {
    const { getDoc: _getDoc, doc: _doc } = await import('@angular/fire/firestore');
    const businessesRef = _doc(this.firestore, CollectionTables.businesses, administratorId);
    const snap = await _getDoc(businessesRef).catch(() => null);
    const collectionName = snap?.exists() ? CollectionTables.businesses : CollectionTables.administrators;
    this.attachEmployeeDocListener(employeeId, administratorId, collectionName);
  }

  private attachEmployeeDocListener(
    employeeId: string,
    administratorId: string,
    collectionName: string
  ): void {
    const empRef = doc(
      this.firestore,
      collectionName,
      administratorId,
      CollectionTables.employees,
      employeeId
    );
    this.employeeDocUnsubscribe = onSnapshot(
      empRef,
      (snap) => {
        if (!snap.exists()) {
          this.stopEmployeeDocListener();
          this.setState({
            role: null,
            employeeId: null,
            administratorId: null,
            permittedProperties: [],
            loaded: true,
          });
          this.accessRevokedSubject.next();
          return;
        }
        const data = snap.data();
        const role = (data?.['role'] ?? 'clerk') as string;
        // Support new businessId field and legacy administratorId field
        const adminId = (data?.['businessId'] || data?.['administratorId'] || '') as string;
        const prev = this.state.value;
        if (
          prev.employeeId === employeeId &&
          prev.role === role &&
          prev.administratorId === adminId
        ) {
          return;
        }
        void this.hydrateEmployeeContext({
          id: employeeId,
          role,
          administratorId: adminId,
        });
      },
      (err) => {
        console.error('UserContextService: employee doc listener error', err);
      }
    );
  }

  private stopEmployeeDocListener(): void {
    this.employeeDocUnsubscribe?.();
    this.employeeDocUnsubscribe = null;
  }

  private setState(partial: Partial<UserContextState>): void {
    const next: UserContextState = { ...this.state.value, ...partial };
    this.state.next(next);
    this.role.set(next.role);
    this.employeeId.set(next.employeeId);
    this.administratorId.set(next.administratorId);
    this.permittedProperties.set(next.permittedProperties);
    this.loaded.set(next.loaded);
  }

  refresh(): void {
    this.stopEmployeeDocListener();
    this.setState({ loaded: false });
    void this.initialize();
  }
}
